import React from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

interface AutoFixConsentModalProps {
  open: boolean;
  loading?: boolean;
  onAccept: () => void;
  onCancel: () => void;
}

export default function AutoFixConsentModal({
  open,
  loading = false,
  onAccept,
  onCancel,
}: AutoFixConsentModalProps) {
  return (
    <Dialog open={open} onClose={onCancel} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <WarningAmberIcon color="warning" />
        Enable Auto-Fix — Remediation Role
      </DialogTitle>

      <DialogContent>
        <Stack spacing={2.5}>
          <div>
            <Typography variant="subtitle2" gutterBottom fontWeight={700}>
              Scan role vs. Remediation role
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Your existing <strong>scan role is read-only</strong> — it can
              only list and describe resources. The <strong>remediation role
              adds scoped write permissions</strong> so Shield can apply
              security fixes you approve (e.g. block public S3 access, revoke
              open security groups, enforce password policies, enable
              CloudTrail logging).
            </Typography>
          </div>

          <Divider />

          <div>
            <Typography variant="subtitle2" gutterBottom fontWeight={700}>
              Tier-1-only scope
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Auto-Fix is limited to <strong>Tier-1 remediations</strong> —
              well-understood, non-destructive hardening actions. Shield will
              never delete data, modify application logic, or access the
              contents of your resources.
            </Typography>
          </div>

          <Divider />

          <div>
            <Typography variant="subtitle2" gutterBottom fontWeight={700}>
              Revocable at any time
            </Typography>
            <Typography variant="body2" color="text.secondary">
              The remediation role is deployed via a CloudFormation stack in
              your account. You can <strong>revoke all write access
              instantly</strong> by deleting that stack — no action needed on
              Shield's side.
            </Typography>
          </div>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <Button
          variant="outlined"
          color="inherit"
          onClick={onCancel}
          disabled={loading}
          sx={{ fontWeight: 600 }}
        >
          Not now
        </Button>
        <Button
          variant="contained"
          color="warning"
          onClick={onAccept}
          disabled={loading}
          sx={{ fontWeight: 700 }}
        >
          {loading
            ? 'Recording consent…'
            : 'I Understand — Continue to Enable Auto-Fix'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
